import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { PickerPeriod } from '@app/components/multi-period-picker';
import {
  FoodActivityRecord,
  FoodOperationActivityRecord,
} from '@shared/api/types';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { ColumnDef } from '@tanstack/react-table';
import { useSoldFoodAndDrinkItem } from '../../sold-food-and-drink-item-context';

export function useSoldFoodAndDrinkItemSalesTableColumns({
  filters,
  timePeriods,
}: {
  filters: FilterOptions<['timePeriods', 'sites']>;
  timePeriods: PickerPeriod[];
}): ColumnDef<FoodOperationActivityRecord>[] {
  const { item } = useSoldFoodAndDrinkItem();
  const hasPackConfiguration = !!item.itemUnit;

  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },

    {
      id: 'period',
      accessorKey: 'timePeriod',
      enableSorting: false,

      header: ({ column }) => <HeaderCell column={column} name="Period" />,
      cell: ({ getValue }) => {
        const period = getValue<FoodActivityRecord['timePeriod']>();
        return <TextCell text={period?.name} />;
      },
      meta: {
        label: 'Time period',
        variant: 'periodPicker',
        periods: timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },

      enableColumnFilter: true,
    },
    {
      id: 'site',
      accessorKey: 'site',
      enableColumnFilter: true,
      enableSorting: false,
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue }) => {
        const site = getValue<FoodActivityRecord['site']>();

        return <TextCell text={site?.name} />;
      },
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'customer',
      accessorKey: 'customer',
      enableSorting: false,
      header: ({ column }) => <HeaderCell column={column} name="Customer" />,
      cell: ({ getValue }) => {
        const customer = getValue<FoodOperationActivityRecord['customer']>();
        return <TextCell text={customer?.name} />;
      },
    },

    ...(hasPackConfiguration
      ? ([
          {
            id: 'quantity',
            accessorKey: 'quantity',
            header: ({ column }) => (
              <HeaderCell column={column} name="Packs sold" numberColumn />
            ),
            cell: ({ getValue, row }) => {
              const recordUnit = row.original.unit?.symbol;
              const quantity = getValue<number>();
              return recordUnit === 'ea' ? (
                <NumberCell number={quantity} />
              ) : (
                <p className="text-right text-muted-foreground">-</p>
              );
            },
          },
        ] as ColumnDef<FoodOperationActivityRecord>[])
      : []),

    {
      id: 'totalMassOrVolume',
      accessorKey: 'totalMassOrVolume',
      header: ({ column }) => (
        <HeaderCell column={column} name="Total weight" numberColumn />
      ),
      cell: ({ getValue }) => {
        const totalWeight = getValue<number>();
        return <NumberCell number={totalWeight} />;
      },
    },
    {
      id: 'unit',
      accessorKey: 'unit',
      enableSorting: false,

      header: ({ column }) => <HeaderCell column={column} name="Unit" />,
      cell: ({ getValue }) => {
        const recordUnit = getValue<FoodActivityRecord['unit']>().symbol;
        const itemUnit = item.itemUnit?.symbol || '';
        return <TextCell text={recordUnit === 'ea' ? itemUnit : recordUnit} />;
      },
      enableColumnFilter: false,
    },
  ];
}
